import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { rgbToHex, hexToRgb, colorDistance, getImageDisplayMetrics } from './colorUtils';

/**
 * Color replacement logic hook
 * - Eyedropper color extraction
 * - Exclude region selection
 * - Color replacement execution
 */
export const useColorReplace = ({ imageRef, previewUrl, selectedMode }) => {
  const [colorMappings, setColorMappings] = useState([]);
  const [isEyedropping, setIsEyedropping] = useState(false);
  const [colorResultUrl, setColorResultUrl] = useState(null);
  const [eyedropPreview, setEyedropPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Exclude regions
  const [excludeRegions, setExcludeRegions] = useState([]);
  const [isDrawingExclude, setIsDrawingExclude] = useState(false);
  const [currentExcludeRegion, setCurrentExcludeRegion] = useState(null);
  const [excludeMode, setExcludeMode] = useState(false);
  const excludeCanvasRef = useRef(null);

  // ===== Exclude region canvas =====
  const drawExcludeRegions = useCallback(() => {
    const canvas = excludeCanvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    const m = getImageDisplayMetrics(image);
    const containerRect = image.getBoundingClientRect();

    canvas.width = containerRect.width;
    canvas.height = containerRect.height;
    canvas.dataset.offsetX = m.offsetX;
    canvas.dataset.offsetY = m.offsetY;
    canvas.dataset.displayWidth = m.displayWidth;
    canvas.dataset.displayHeight = m.displayHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    [...excludeRegions, currentExcludeRegion].filter(Boolean).forEach((region, index) => {
      const x = m.offsetX + region.x * m.displayWidth;
      const y = m.offsetY + region.y * m.displayHeight;
      const w = region.width * m.displayWidth;
      const h = region.height * m.displayHeight;

      ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.7)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, w, h);

      if (region !== currentExcludeRegion) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
        ctx.font = 'bold 11px sans-serif';
        ctx.fillText(`Exclude ${index + 1}`, x + 4, y + 14);
      }
    });
  }, [excludeRegions, currentExcludeRegion, imageRef]);

  useEffect(() => { drawExcludeRegions(); }, [drawExcludeRegions]);

  const getExcludeCoordinates = (e) => {
    const image = imageRef.current;
    if (!image) return null;
    const m = getImageDisplayMetrics(image);
    const mouseX = e.clientX - m.rect.left;
    const mouseY = e.clientY - m.rect.top;
    if (mouseX < m.offsetX || mouseX > m.offsetX + m.displayWidth ||
        mouseY < m.offsetY || mouseY > m.offsetY + m.displayHeight) return null;
    return { x: (mouseX - m.offsetX) / m.displayWidth, y: (mouseY - m.offsetY) / m.displayHeight };
  };

  const handleExcludeMouseDown = (e) => {
    const coords = getExcludeCoordinates(e);
    if (!coords) return;
    setIsDrawingExclude(true);
    setCurrentExcludeRegion({ x: coords.x, y: coords.y, width: 0, height: 0 });
  };

  const handleExcludeMouseMove = (e) => {
    if (!isDrawingExclude || !currentExcludeRegion) return;
    const coords = getExcludeCoordinates(e);
    if (!coords) return;
    setCurrentExcludeRegion(prev => ({
      ...prev,
      width: Math.max(0.01, coords.x - prev.x),
      height: Math.max(0.01, coords.y - prev.y),
    }));
  };

  const handleExcludeMouseUp = () => {
    if (!isDrawingExclude || !currentExcludeRegion) return;
    if (currentExcludeRegion.width > 0.02 && currentExcludeRegion.height > 0.02) {
      setExcludeRegions(prev => [...prev, currentExcludeRegion]);
    }
    setIsDrawingExclude(false);
    setCurrentExcludeRegion(null);
  };

  // ===== Eyedropper =====
  const handleEyedropMove = (e) => {
    if (selectedMode !== 'color' || !isEyedropping || excludeMode || colorResultUrl) {
      if (eyedropPreview) setEyedropPreview(null);
      return;
    }
    const image = imageRef.current;
    if (!image) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = image.naturalWidth;
    tempCanvas.height = image.naturalHeight;
    const ctx = tempCanvas.getContext('2d');
    ctx.drawImage(image, 0, 0);

    const m = getImageDisplayMetrics(image);
    const mx = e.clientX - m.rect.left, my = e.clientY - m.rect.top;
    if (mx < m.offsetX || mx > m.offsetX + m.displayWidth || my < m.offsetY || my > m.offsetY + m.displayHeight) {
      setEyedropPreview(null);
      return;
    }

    const imgX = Math.floor(((mx - m.offsetX) / m.displayWidth) * m.naturalWidth);
    const imgY = Math.floor(((my - m.offsetY) / m.displayHeight) * m.naturalHeight);
    const pixel = ctx.getImageData(imgX, imgY, 1, 1).data;
    const hex = rgbToHex(pixel[0], pixel[1], pixel[2]);

    const container = image.parentElement.getBoundingClientRect();
    setEyedropPreview({ x: e.clientX - container.left, y: e.clientY - container.top, color: hex });
  };

  const handleEyedropLeave = () => setEyedropPreview(null);

  const handleEyedropClick = (e) => {
    if (selectedMode !== 'color' || !previewUrl || colorResultUrl) return;
    if (!isEyedropping || excludeMode) return;

    const image = imageRef.current;
    if (!image) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = image.naturalWidth;
    tempCanvas.height = image.naturalHeight;
    const ctx = tempCanvas.getContext('2d');
    ctx.drawImage(image, 0, 0);

    const m = getImageDisplayMetrics(image);
    const mouseX = e.clientX - m.rect.left;
    const mouseY = e.clientY - m.rect.top;
    if (mouseX < m.offsetX || mouseX > m.offsetX + m.displayWidth ||
        mouseY < m.offsetY || mouseY > m.offsetY + m.displayHeight) return;

    const imgX = Math.floor(((mouseX - m.offsetX) / m.displayWidth) * m.naturalWidth);
    const imgY = Math.floor(((mouseY - m.offsetY) / m.displayHeight) * m.naturalHeight);

    const pixel = ctx.getImageData(imgX, imgY, 1, 1).data;
    const hex = rgbToHex(pixel[0], pixel[1], pixel[2]);

    if (colorMappings.some(m => m.original === hex)) {
      toast('Color already added', { icon: '🎨' });
      return;
    }

    setColorMappings(prev => [...prev, { original: hex, replacement: '#FF0000', tolerance: 50 }]);
    setIsEyedropping(false);
    toast.success(`Color ${hex} picked`);
  };

  // ===== Color mapping management =====
  const updateColorMapping = (index, field, value) => {
    setColorMappings(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };
  const removeColorMapping = (index) => {
    setColorMappings(prev => prev.filter((_, i) => i !== index));
  };

  // ===== Color replacement execution =====
  const handleColorReplace = () => {
    if (!previewUrl || colorMappings.length === 0) return;
    setIsProcessing(true);

    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const imgW = canvas.width, imgH = canvas.height;
      const original = new Uint8ClampedArray(data);

      for (const mapping of colorMappings) {
        const orig = hexToRgb(mapping.original);
        const repl = hexToRgb(mapping.replacement);
        if (!orig || !repl) continue;
        const tol = mapping.tolerance, outerTol = tol * 1.4;

        for (let i = 0; i < data.length; i += 4) {
          if (excludeRegions.length > 0) {
            const pi = i / 4;
            const px = (pi % imgW) / imgW;
            const py = Math.floor(pi / imgW) / imgH;
            if (excludeRegions.some(r => px >= r.x && px <= r.x + r.width && py >= r.y && py <= r.y + r.height)) continue;
          }
          const r = original[i], g = original[i + 1], b = original[i + 2];
          const dist = colorDistance(r, g, b, orig.r, orig.g, orig.b);
          if (dist <= outerTol) {
            let blend;
            if (dist <= tol * 0.6) blend = 1;
            else if (dist <= tol) blend = 1 - ((dist - tol * 0.6) / (tol * 0.4)) * 0.3;
            else blend = 0.7 * (1 - (dist - tol) / (outerTol - tol));
            blend = Math.max(0, Math.min(1, blend));
            data[i] = Math.round(r * (1 - blend) + repl.r * blend);
            data[i + 1] = Math.round(g * (1 - blend) + repl.g * blend);
            data[i + 2] = Math.round(b * (1 - blend) + repl.b * blend);
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      setColorResultUrl(canvas.toDataURL('image/png'));
      setIsProcessing(false);
      toast.success('Color change complete!');
    };
    image.onerror = () => { setIsProcessing(false); toast.error('Failed to process image'); };
    image.src = previewUrl;
  };

  // ===== Copy / Download result =====
  const handleCopyColorResult = async () => {
    if (!colorResultUrl) return;
    try {
      const res = await fetch(colorResultUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('Image copied to clipboard');
    } catch { toast.error('Failed to copy image'); }
  };

  const handleDownloadColorResult = () => {
    if (!colorResultUrl) return;
    const a = document.createElement('a');
    a.href = colorResultUrl;
    a.download = 'chart_recolored.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Image downloaded');
  };

  const clearColorResult = () => setColorResultUrl(null);

  const resetColor = () => {
    setColorMappings([]);
    setColorResultUrl(null);
    setIsEyedropping(false);
    setExcludeRegions([]);
    setExcludeMode(false);
  };

  return {
    // color mappings
    colorMappings, setColorMappings,
    isEyedropping, setIsEyedropping,
    colorResultUrl, setColorResultUrl,
    eyedropPreview,
    isProcessing,
    // exclude regions
    excludeRegions, setExcludeRegions,
    excludeMode, setExcludeMode,
    excludeCanvasRef,
    handleExcludeMouseDown, handleExcludeMouseMove, handleExcludeMouseUp,
    // eyedropper
    handleEyedropMove, handleEyedropLeave, handleEyedropClick,
    // mapping management
    updateColorMapping, removeColorMapping,
    // execution
    handleColorReplace,
    handleCopyColorResult, handleDownloadColorResult,
    clearColorResult, resetColor,
  };
};
